import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SolarScene from "./SolarScene";
import { useSite } from "@/context/SiteContext";
import { API_URL } from "@/lib/api";

function convex(points) {
  if (points.length !== 4) return false;
  const signs = [];
  for (let i = 0; i < 4; i++) {
    const a = points[i], b = points[(i + 1) % 4], c = points[(i + 2) % 4];
    signs.push((b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x));
  }
  return signs.every((x) => x > 10) || signs.every((x) => x < -10);
}

function interpolate(corners, u, v) {
  const [a, b, c, d] = corners;
  return {
    x: (1 - u) * (1 - v) * a.x + u * (1 - v) * b.x + u * v * c.x + (1 - u) * v * d.x,
    y: (1 - u) * (1 - v) * a.y + u * (1 - v) * b.y + u * v * c.y + (1 - u) * v * d.y,
  };
}

export default function RoofStudio({ initialAddress = "" }) {
  const { openLead, setFunnelExtra } = useSite();
  const navigate = useNavigate();
  const [address, setAddress] = useState(initialAddress);
  const [length, setLength] = useState(120);
  const [width, setWidth] = useState(80);
  const [coverage, setCoverage] = useState(65);
  const [units, setUnits] = useState("ft");
  const [view, setView] = useState("model");
  const [batteries, setBatteries] = useState(2);
  const [showPanels, setShowPanels] = useState(true);
  const [photoStatus, setPhotoStatus] = useState("");
  const [mapsStatus, setMapsStatus] = useState("");
  const [photoURL, setPhotoURL] = useState(null);
  const [corners, setCorners] = useState([]);
  const photoImage = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  function dimensions() {
    const cap = units === "ft" ? 1000 : 305;
    return {
      length: Math.min(cap, Math.max(units === "ft" ? 10 : 3, Number(length) || 10)),
      width: Math.min(cap, Math.max(units === "ft" ? 10 : 3, Number(width) || 10)),
      coverage,
      batteries,
      units,
    };
  }

  function pushFunnel(next) {
    const d = next || dimensions();
    setFunnelExtra({
      address,
      dimensions: { length: d.length, width: d.width, units, coverage },
      layoutSourceMode: photoImage.current && corners.length === 4 ? "photo" : "model",
      roofSegments: corners.length === 4 ? [{ length: d.length, width: d.width }] : null,
      storageInterest: { goal: batteries > 0 ? "Solar + storage" : "Solar only", units: batteries },
    });
  }

  function redraw(nextCorners) {
    const image = photoImage.current;
    const canvas = canvasRef.current;
    if (!image || !canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = 1000;
    canvas.height = Math.round((1000 * image.height) / image.width);
    if (canvas.height > 1600) {
      canvas.height = 1600;
      canvas.width = Math.round((1600 * image.width) / image.height);
    }
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    const pts = nextCorners ?? corners;
    if (pts.length === 4 && convex(pts)) {
      const factor = Math.sqrt(coverage / 100), start = (1 - factor) / 2, end = 1 - start;
      const cols = 8, rows = 5;
      for (let i = 0; i < cols; i++)
        for (let j = 0; j < rows; j++) {
          const u0 = start + ((end - start) * (i + 0.06)) / cols, u1 = start + ((end - start) * (i + 0.94)) / cols;
          const v0 = start + ((end - start) * (j + 0.06)) / rows, v1 = start + ((end - start) * (j + 0.94)) / rows;
          const p = [interpolate(pts, u0, v0), interpolate(pts, u1, v0), interpolate(pts, u1, v1), interpolate(pts, u0, v1)];
          ctx.beginPath();
          p.forEach((q, k) => (k ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y)));
          ctx.closePath();
          ctx.fillStyle = "rgba(10,49,75,.86)";
          ctx.fill();
          ctx.strokeStyle = "#91bfd0";
          ctx.lineWidth = 1;
          ctx.stroke();
        }
    }
    ctx.fillStyle = "#d2f58b";
    ctx.strokeStyle = "#d2f58b";
    ctx.lineWidth = 3;
    pts.forEach((p, i) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = "bold 22px sans-serif";
      ctx.fillText(i + 1, p.x + 12, p.y);
    });
  }

  function onPhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 15 * 1024 * 1024) {
      setPhotoStatus("Choose a JPG, PNG or WebP image under 15 MB.");
      return;
    }
    if (photoURL) URL.revokeObjectURL(photoURL);
    const url = URL.createObjectURL(file);
    setPhotoURL(url);
    const image = new Image();
    image.onload = () => {
      photoImage.current = image;
      setCorners([]);
      setPhotoStatus("Photo ready. Mark four corners of a clear roof plane.");
      setView("photo");
      setTimeout(() => redraw([]), 30);
    };
    image.onerror = () => setPhotoStatus("This image could not be opened. Try a JPG or PNG.");
    image.src = url;
  }

  function canvasClick(e) {
    if (!photoImage.current || corners.length === 4) return;
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const next = [...corners, { x: ((e.clientX - r.left) * c.width) / r.width, y: ((e.clientY - r.top) * c.height) / r.height }];
    setCorners(next);
    redraw(next);
  }

  const d = dimensions();
  const area = Math.round(d.length * d.width);

  async function lookup() {
    if (!address.trim()) {
      setMapsStatus("Enter the building address first. You can still try dimensions or upload a photo.");
      return;
    }
    setMapsStatus("Checking rooftop data…");
    try {
      const res = await fetch(`${API_URL}/roof/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address }),
      });
      const data = await res.json();
      if (data.status === "FOUND") {
        setMapsStatus(`Confirmed: ${data.building?.name || "building"} · Imagery ${data.imageryDate?.year || "n/a"} · Source: Includes solar data from Google.`);
      } else {
        setMapsStatus(data.message || "Live map lookup is not connected yet. Continue with dimensions or add a roof photo.");
      }
    } catch {
      setMapsStatus("Rooftop lookup is unavailable right now. Continue with dimensions or add a roof photo.");
    }
    pushFunnel();
  }

  const sceneState = { length: d.length, width: d.width, coverage, batteries };

  return (
    <div className="visualizer-grid" data-testid="roof-studio">
      <div className="panel">
        <div className="steps">
          <span>1. Property</span>
          <span>2. Layout</span>
          <span>3. Energy plan</span>
        </div>
        <h3>Your roof, your starting point.</h3>
        <label className="field">
          Property address
          <input placeholder="Street, city, state, ZIP" autoComplete="street-address" data-testid="roof-address" value={address} onChange={(e) => setAddress(e.target.value)} onBlur={pushFunnel} />
        </label>
        <div className="twofields">
          <label className="field">
            Length ({units})
            <input type="number" value={length} min={units === "ft" ? 10 : 3} max={units === "ft" ? 1000 : 305} data-testid="roof-length" onChange={(e) => setLength(e.target.value)} />
          </label>
          <label className="field">
            Width ({units})
            <input type="number" value={width} min={units === "ft" ? 10 : 3} max={units === "ft" ? 1000 : 305} data-testid="roof-width" onChange={(e) => setWidth(e.target.value)} />
          </label>
        </div>
        <div className="twofields">
          <label className="field">
            Units
            <select data-testid="roof-units" value={units} onChange={(e) => setUnits(e.target.value)}>
              <option value="ft">Feet</option>
              <option value="m">Meters</option>
            </select>
          </label>
          <label className="field">
            Battery interest
            <select data-testid="roof-battery" value={batteries} onChange={(e) => setBatteries(Number(e.target.value))}>
              <option value={2}>Solar + storage</option>
              <option value={0}>Solar only</option>
              <option value={3}>Explore more backup</option>
            </select>
          </label>
        </div>
        <div className="range-label">
          <b>Concept coverage</b>
          <span data-testid="coverage-value">{coverage}%</span>
        </div>
        <input type="range" className="slider" min={10} max={85} value={coverage} aria-label="Concept panel coverage" data-testid="roof-coverage" onChange={(e) => setCoverage(Number(e.target.value))} />
        <div className="twofields">
          <label className="field">
            View
            <select data-testid="roof-view" value={view} onChange={(e) => setView(e.target.value)}>
              <option value="model">3D concept</option>
              <option value="photo">My building photo</option>
            </select>
          </label>
          <label className="field">
            &nbsp;
            <button className="btn light" style={{ width: "100%", marginTop: 6 }} onClick={() => fileRef.current?.click()} data-testid="roof-photo-button">
              ↥ Add a roof photo
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={onPhoto} data-testid="roof-photo-input" />
          </label>
        </div>
        <div className="status" aria-live="polite" data-testid="photo-status">{photoStatus}</div>
        <button className="btn" style={{ width: "100%", marginTop: 17 }} onClick={lookup} data-testid="maps-lookup">
          Find my roof on the map <span>↗</span>
        </button>
        <p className="micro" aria-live="polite" data-testid="maps-status">
          {mapsStatus || "This concept uses your dimensions. Confirm the building on the live map where coverage allows."}
        </p>
      </div>
      <div>
        {view === "model" ? (
          <div className="model-box" id="model-box" data-testid="roof-model-box">
            <div className="modeltop">
              <strong>YOUR SOLAR CONCEPT</strong>
              <button className="chip" aria-pressed={showPanels} data-testid="panels-toggle" onClick={() => setShowPanels(!showPanels)}>
                {showPanels ? "Hide panels" : "Show panels"}
              </button>
            </div>
            <SolarScene kind="model" state={{ ...sceneState, showPanels }} testId="roof-model-scene" />
            <div className="modelbottom">
              <div className="metric">
                <span>Roof footprint</span>
                <strong data-testid="roof-area">{area.toLocaleString()} {units === "ft" ? "ft²" : "m²"}</strong>
              </div>
              <div className="metric">
                <span>Concept coverage</span>
                <strong data-testid="roof-cover">{Math.round((area * coverage) / 100).toLocaleString()} {units === "ft" ? "ft²" : "m²"}</strong>
              </div>
              <button
                className="btn lime"
                data-testid="review-layout"
                onClick={() => {
                  pushFunnel();
                  openLead("commercial", "Rooftop solar visualization", { audience: "business" });
                }}
              >
                Review my layout ↗
              </button>
            </div>
          </div>
        ) : (
          <div className="photobox" id="photo-box" data-testid="roof-photo-box">
            <div className="photohelp">
              Click the four corners of one clear roof plane, clockwise.{" "}
              <button className="textbutton" data-testid="reset-corners" onClick={() => { setCorners([]); redraw([]); }}>
                Reset corners
              </button>
              <br />
              <span data-testid="corner-status">{corners.length} / 4 corners selected{corners.length === 4 && !convex(corners) ? " · Select corners around the roof perimeter without crossing." : ""}</span>
            </div>
            <canvas ref={canvasRef} width={1000} height={650} aria-label="Your uploaded building with concept panel overlay" onClick={canvasClick} data-testid="photo-canvas" />
            <p className="micro">Concept only. Manual placement does not measure slope, shade or structural suitability. Your photo stays private and is only submitted if you request an assessment.</p>
          </div>
        )}
        <p className="caption">
          Illustrative layout based on your inputs. A site survey checks roof condition, usable area, obstructions, setbacks and electrical capacity.
        </p>
      </div>
    </div>
  );
}
